import type {ReactNode} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import {ContentBlockHeading} from '@/modules/foundation/components/content-block-heading';
import {GridList} from '@/modules/foundation/components/grid-list';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';
import type {FinknIcons} from '@/shared/renderIcon';
import {HoverIcon} from '@/shared/renderIcon';

interface Props extends ContentBlockContainerProps {
	heading?: string;
	description?: ReactNode;
	nemnds?: {
		_id: string;
		name: string;
		href: string;
		description: ReactNode;
		icon?: FinknIcons;
	}[];
	anchorHeadings?: boolean;
	insideToc?: boolean;
}

export const nemndListAllDescriptionPortableTextOverrides: PortableTextOverrides = {
	p: 'text-xl',
};

export const NemndListAll = ({
	heading,
	description,
	nemnds,
	anchorHeadings,
	options,
	insideToc,
}: Props) => {
	return (
		<ContentBlockContainer as="section" options={options} insideToc={insideToc}>
			<div className="@container">
				<ContentBlockHeading anchorHeading={anchorHeadings} heading={heading} textCenter>
					{description}
				</ContentBlockHeading>

				<GridList>
					{nemnds?.map((nemnd) => (
						<li key={nemnd._id}>
							<Link
								href={nemnd.href}
								className={clsx(
									'block',
									'bg-white',
									'shadow-card',
									'p-6',
									'group',
									'rounded-[3px]',
									'h-full',
								)}
							>
								<HoverIcon icon={nemnd.icon} className="mb-8" />
								<Heading
									level={heading ? 3 : 2}
									size="md"
									spacing
									className={clsx(
										'flex',
										'items-center',
										'justify-between',
										'gap-4',
										'transition-colors',
										'group-hover:text-primary-15',
									)}
								>
									{nemnd.name}
									<ArrowRightIcon
										aria-hidden
										className={clsx(
											'text-grey-25',
											'text-base',
											'shrink-0',
											'transition-all',
											'group-hover:text-primary-15',
											'mr-2',
											'group-hover:mr-0',
										)}
									/>
								</Heading>
								<div className={clsx('text-grey-15')}>{nemnd.description}</div>
							</Link>
						</li>
					))}
				</GridList>
			</div>
		</ContentBlockContainer>
	);
};
